import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminPaygEmailDialogComponent } from '../admin-payg-email-dialog/admin-payg-email-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminPaygMessageDialogComponent } from './admin-payg-message-dialog/admin-payg-message-dialog.component';

export interface PaygCandidate {

  id: number;

  probateId: string;
  bequestId: number;
  beneficiaryId: number;

  beneficiaryName: string;
  beneficiaryURN: string;

  email?: string;

  aiEmail?: string;
  aiEmailCategory?: string;
  aiStatus?: string;
  filename?: string;


  bequestType?: string;

  estateValue?: number;

  cashAmount?: number;
  percentageAmount?: number;

  bequestValue?: number;

  verificationDate?: string;
  subscribed?: boolean;
  sendEmail: boolean;
  issueDate?: string;

  createdDate?: string;

  issueDateFrom?: Date;
  issueDateTo?: Date;
}


export interface PaygEmailAudit {
  id: number;

  bequestId: number;
  beneficiaryId: number;

  email?: string;

  subjectTemplate?: string;
  bodyTemplate?: string;

  subjectRendered?: string;
  bodyRendered?: string;

  sentDate?: string;

  status?: string;

  errorMessage?: string;

  sentBy?: string;

  azureOperationId?: string;
}


export interface PaygPaid {
  id: number;

  bequestId: number;
  beneficiaryId: number;

  probateId?: string;

  beneficiaryName?: string;
  beneficiaryURN?: string;

  email?: string;
  aiEmail?: string;

  amount: number;
  currency?: string;

  stripeCheckoutSessionId?: string;

  createdDate?: string;
  expiryDate?: string;
  paidDate?: string;

  status?: string;
}




@Component({
  selector: 'app-admin-payg',
  templateUrl: './admin-payg.component.html',
  styleUrl: './admin-payg.component.css',
  standalone: false
})
export class AdminPaygComponent {

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  @ViewChild(MatPaginator)
  paginator!: MatPaginator;

  paygCases: PaygCandidate[] = [];

  filteredPaygCases: PaygCandidate[] = [];

  pagedPaygCases: PaygCandidate[] = [];

  searchTerm = "";

  showWithEmail = true;
  showWithoutEmail = false;

  showWithAIEmail = true;
  showWithoutAIEmail = false;

  showFoundAIEmail = true;
  issueDateFilter: 'all' | 'recent' | 'archived' = 'recent';
  bequestTypes: any[] = [];

  selectedBequestTypes: string[] = [];

  minEstateValue?: number;
  maxEstateValue?: number;

  issueDateFrom?: Date;

  issueDateTo?: Date;
  pageSize = 50;

  cashAmount?: number;

  percentageAmount?: number;

  activeTab = 'candidates';

  paygEmails: PaygEmailAudit[] = [];

  paygPaid: PaygPaid[] = [];

  ngOnInit() {

    this.getPaygCandidates();

  }

  get selectedCount(): number {

    return this.filteredPaygCases.filter(x => x.sendEmail).length;

  }

  private showMessage(
    title: string,
    message: string,
    type: 'success' | 'error' | 'info' = 'info'
  ): void {

    this.dialog.open(
      AdminPaygMessageDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        disableClose: true,
        data: {
          title,
          message,
          type
        }
      }
    );

  }


  get withEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => x.email && x.email.trim() !== "")
      .length;
  }

  get withoutEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => !x.email || x.email.trim() === "")
      .length;
  }

  get withAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => x.aiEmail && x.aiEmail.trim() !== "")
      .length;
  }

  get withoutAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x => !x.aiEmail || x.aiEmail.trim() === "")
      .length;
  }

  get foundAIEmailCount(): number {
    return this.filterCandidates(false)
      .filter(x =>
        x.aiStatus?.toLowerCase() === "found"
      )
      .length;
  }

  openEmailDialog() {

    const selected = this.filteredPaygCases.filter(
      x => x.sendEmail && !this.isArchived(x)
    );

    if (selected.length === 0) {

      this.showMessage(
        'No Recipients Selected',
        'Please select at least one eligible recipient.',
        'info'
      );

      return;
    }

    const dialogRef = this.dialog.open(
      AdminPaygEmailDialogComponent,
      {
        width: '1000px',
        maxWidth: '95vw',
        data: selected
      }
    );

    dialogRef.afterClosed().subscribe(result => {

      if (!result) {
        return;
      }

      this.http.post(
        '/charitycentral/sendpaygemails',
        result
      ).subscribe({

        next: (response: any) => {

          const queued = response?.queued ?? 0;

          this.showMessage(
            'Emails Queued Successfully',
            `${queued} email(s) have been queued successfully.`,
            'success'
          );

          this.getPaygCandidates();
          this.getPaygEmails();

        },

        error: (error) => {

          console.error(
            'Failed to queue PAYG emails',
            error
          );

          this.showMessage(
            'PAYG Emails Not Sent',
            error?.error?.message ||
            'Failed to queue PAYG emails.',
            'error'
          );

        }

      });

    });

  }

  isArchived(candidate: PaygCandidate): boolean {
    return !!candidate.filename &&
      candidate.filename.trim().toUpperCase().startsWith('X');
  }

  getPaygCandidates() {

    this.http.get<PaygCandidate[]>("/charitycentral/getpaygcandidates").subscribe(

      (result) => {

        this.paygCases = (result ?? []).map(x => ({
          ...x,
          bequestValue: this.calculateBequestValue(x),
          sendEmail: !this.isArchived(x)
        }));

        this.bequestTypes = [...new Set(
          this.paygCases
            .map(x => x.bequestType)
            .filter(x => x != null && x !== "")
        )]
          .sort();

        this.applyFilter();

      },

      (error) => {

        console.error(error);

      }

    );

  }

  refresh() {

    if (this.selectedTabIndex === 0) {
      this.getPaygCandidates();
    }

    if (this.selectedTabIndex === 1) {
      this.getPaygEmails();
    }

    if (this.selectedTabIndex === 2) {
      this.getPaygPaid();
    }

  }

  applyFilter() {

    this.filteredPaygCases = this.filterCandidates(true);

    this.updatePagedData();

  }

  private filterCandidates(includeEmailFilter: boolean): PaygCandidate[] {

    const term = this.searchTerm.trim().toLowerCase();

    return this.paygCases.filter(x => {

      // -----------------------------
      // Email filter
      // -----------------------------

      if (includeEmailFilter) {

        const hasEmail =
          x.email != null &&
          x.email.trim() !== "";

        const hasAIEmail =
          x.aiEmail != null &&
          x.aiEmail.trim() !== "";

        const emailMatches =
          (hasEmail && this.showWithEmail) ||
          (!hasEmail && this.showWithoutEmail);

        const aiEmailMatches =
          (hasAIEmail && this.showWithAIEmail) ||
          (!hasAIEmail && this.showWithoutAIEmail);

        if (!emailMatches || !aiEmailMatches) {
          return false;
        }

        if (
          this.showFoundAIEmail &&
          x.aiStatus?.toLowerCase() !== "found"
        ) {
          return false;
        }

      }

      // -----------------------------
      // Bequest Type filter
      // -----------------------------

      const bequestMatches =
        this.selectedBequestTypes.length === 0 ||
        this.selectedBequestTypes.includes(x.bequestType ?? "");

      if (!bequestMatches) {
        return false;
      }

      // -----------------------------
      // Estate Value filter
      // -----------------------------

      const estate = x.estateValue ?? 0;

      if (
        this.minEstateValue != null &&
        estate < this.minEstateValue
      ) {
        return false;
      }

      if (
        this.maxEstateValue != null &&
        estate > this.maxEstateValue
      ) {
        return false;
      }

     // -----------------------------
// Issue Date filter
// -----------------------------

if (x.issueDate) {

  const issueDate = new Date(x.issueDate);

  issueDate.setHours(0, 0, 0, 0);

  // Recent / Archived filter
  const issueYear = issueDate.getFullYear();

  if (
    this.issueDateFilter === 'recent' &&
    issueYear !== 2026
  ) {
    return false;
  }

  if (
    this.issueDateFilter === 'archived' &&
    issueYear >= 2026
  ) {
    return false;
  }

  // Existing From / To date filters
  if (this.issueDateFrom) {

    const from = new Date(this.issueDateFrom);

    from.setHours(0, 0, 0, 0);

    if (issueDate < from) {
      return false;
    }

  }

  if (this.issueDateTo) {

    const to = new Date(this.issueDateTo);

    to.setHours(0, 0, 0, 0);

    if (issueDate > to) {
      return false;
    }

  }

}
else if (
  this.issueDateFrom ||
  this.issueDateTo ||
  this.issueDateFilter !== 'all'
) {

  return false;

}

      // -----------------------------
      // Search
      // -----------------------------

      if (term === "") {
        return true;
      }

      return (
        x.probateId?.toString().includes(term) ||
        x.beneficiaryName?.toLowerCase().includes(term) ||
        x.beneficiaryURN?.toLowerCase().includes(term) ||
        x.email?.toLowerCase().includes(term) ||
        x.aiEmail?.toLowerCase().includes(term)
      );

    });

  }

  selectAll(): void {
    this.filteredPaygCases.forEach(x => {
      x.sendEmail = !this.isArchived(x);
    });
  }

  deselectAll(): void {
    this.filteredPaygCases.forEach(x => x.sendEmail = false);
  }
  get allSelected(): boolean {

    const eligibleCandidates =
      this.filteredPaygCases.filter(x => !this.isArchived(x));

    return eligibleCandidates.length > 0 &&
      eligibleCandidates.every(x => x.sendEmail);

  }
  
  get hasArchivedCandidates(): boolean {
    return this.pagedPaygCases.some(x => this.isArchived(x));
  }


  selectedTabIndex = 0;

  tabChanged(index: number) {

    this.selectedTabIndex = index;

    if (index === 0) {
      this.getPaygCandidates();
    }

    if (index === 1) {
      this.getPaygEmails();
    }

    if (index === 2) {
      this.getPaygPaid();
    }

  }



  pageChanged(event: PageEvent) {

    this.pageSize = event.pageSize;

    const start = event.pageIndex * event.pageSize;

    const end = start + event.pageSize;

    this.pagedPaygCases = this.filteredPaygCases.slice(start, end);

  }

  private updatePagedData() {

    this.pagedPaygCases = this.filteredPaygCases.slice(0, this.pageSize);

    if (this.paginator) {

      this.paginator.firstPage();

    }

  }



private calculateBequestValue(candidate: PaygCandidate): number | undefined {

  if (candidate.bequestType === "Absolute Pecuniary") {
    return candidate.cashAmount;
  }

  if (candidate.bequestType === "Absolute Residual") {
    return candidate.percentageAmount;
  }

  return candidate.cashAmount;
}


  getPaygEmails() {

    this.http
      .get<PaygEmailAudit[]>("/charitycentral/getpaygemails")
      .subscribe({

        next: (result) => {

          this.paygEmails = result ?? [];

        },

        error: (error) => {

          console.error("Failed to load PAYG emails", error);

        }

      });

  }


  getPaygPaid() {

    this.http
      .get<PaygPaid[]>("/charitycentral/getpaygpaid")
      .subscribe({

        next: (result) => {

          this.paygPaid = result ?? [];

        },

        error: (error) => {

          console.error("Failed to load PAYG paid payments", error);

        }

      });

  }

}
